import { Outlet, NavLink, useLocation, useNavigate } from "react-router";
import { Toaster, toast } from "sonner";
import {
  Menu,
  X,
  CreditCard,
  FileText,
  Globe,
  LogOut,
  MessageSquarePlus,
  RotateCw,
  Settings,
  ShieldCheck,
} from "lucide-react";
import { Suspense, useEffect, useRef, useState } from "react";

import { Watch } from "./Watch";
import { NotificationBell } from "./notifications/NotificationBell";
import { FeedbackLauncher } from "./feedback/FeedbackLauncher";
import { useCompanion } from "./companion/CompanionProvider";
import { AskBar } from "./AskBar";
import { ExperienceToggle } from "./ExperienceToggle";
// Sales prompt turned off per Peter 2026-06-18 — re-enable with the render
// line near <Outlet/> below.
// import { SalesPrompt } from "./trial/SalesPrompt";
import { TrialBadge } from "./trial/TrialBadge";
import { SUPPORTED_LOCALES, useI18n, useT } from "../../lib/i18n";
import { useAuth } from "../AuthContext";
import { NAV_ITEMS, navIcon, type NavSection } from "../navConfig";
import { listModels } from "../../lib/models";
import { fetchUnreadActivityCount } from "../../lib/notifications";
import type { AiUsageEventDetail } from "../../lib/api";

// Primary nav lives in a LEFT SIDEBAR, grouped into two sections that
// mirror the Today design language:
//   Workspace — Today, Plans, Ask a question, Data
//   Library   — Decisions and grounded signals
// Settings anchors the sidebar foot. The signed-in user, refresh, and
// notification bell now live in a TOP BAR (see TopBar below) rather than
// the sidebar footer, matching the Today mockup.
// Primary nav is defined ONCE in ../navConfig and shared with the V3 /
// Data 2 preview shells, so the sidebars can never drift apart again.
// This classic shell renders the full list (previews included).

const SECTION_LABELS: Record<NavSection, string> = {
  workspace: "Workspace",
  library: "Decisions",
};

// Polling cadence for workspace state probes (model presence + MR
// activity), matched to the bell's closed cadence.
const NAV_POLL_MS = 30_000;

function formatUsageTokens(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k.toFixed(k < 10 ? 1 : 0).replace(/\.0$/, "")}k`;
}

export function RootLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const { me, logout } = useAuth();
  const showWatch = location.pathname !== "/ask-meridian";
  const workspaceId = me?.workspace?.id ?? null;
  // When the Companion layer is active it subsumes Watch + the NotificationBell
  // into one presence, so the top-bar widgets stand down. Off → unchanged.
  const companion = useCompanion();
  const companionOn = companion?.enabled ?? false;

  const [hasModels, setHasModels] = useState<boolean | undefined>(undefined);
  const [activityCount, setActivityCount] = useState(0);
  const [mobileNav, setMobileNav] = useState(false);
  useEffect(() => { setMobileNav(false); }, [location.pathname]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    async function poll() {
      try {
        const models = await listModels(workspaceId!);
        if (!cancelled) setHasModels(models.length > 0);
      } catch {
        /* network blip — keep previous answer */
      }
      try {
        const n = await fetchUnreadActivityCount();
        if (!cancelled) setActivityCount(n);
      } catch {
        /* ignored */
      }
    }
    void poll();
    const id = window.setInterval(() => void poll(), NAV_POLL_MS);
    const onModelCreated = () => void poll();
    window.addEventListener("meridian:model-created", onModelCreated);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("meridian:model-created", onModelCreated);
    };
  }, [workspaceId]);

  useEffect(() => {
    function onAiUsage(ev: Event) {
      const detail = (ev as CustomEvent<AiUsageEventDetail>).detail;
      if (!detail || detail.totalTokens <= 0) return;
      const cached =
        detail.cachedTokens > 0 ? ` · ${formatUsageTokens(detail.cachedTokens)} cached` : "";
      const calls = detail.aiCalls > 1 ? ` · ${detail.aiCalls} calls` : "";
      toast("AI usage", {
        description:
          `${formatUsageTokens(detail.totalTokens)} total · ` +
          `${formatUsageTokens(detail.inputTokens)} in · ` +
          `${formatUsageTokens(detail.outputTokens)} out` +
          `${cached}${calls}`,
        duration: 3200,
      });
    }
    window.addEventListener("meridian:ai-usage", onAiUsage as EventListener);
    return () =>
      window.removeEventListener("meridian:ai-usage", onAiUsage as EventListener);
  }, []);

  const visibleNav = NAV_ITEMS.filter((item) => {
    if (!item.requiresModel) return true;
    return hasModels === true;
  });
  const primaryPaths = new Set(["/home", "/sales-overview", "/ask-meridian", "/data", "/alerts", "/insights"]);
  const additionalNav = visibleNav.filter(item => !primaryPaths.has(item.path));

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const accent = me?.workspace?.accent_color || "#0f172a";
  const customLogo = me?.workspace?.logo_url || null;

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* LEFT SIDEBAR — grouped primary nav + utilities. */}
      {mobileNav && <button className="fixed inset-0 z-30 bg-slate-900/30 md:hidden" aria-label="Close navigation" onClick={() => setMobileNav(false)} />}
      <aside id="main-navigation" onKeyDown={(e) => { if (e.key === "Escape") setMobileNav(false); }} className={`${mobileNav ? "flex" : "hidden"} fixed top-0 z-40 h-screen w-[220px] shrink-0 flex-col border-r border-slate-200 bg-white md:sticky md:z-30 md:flex`}>
        {mobileNav && <button className="absolute right-2 top-2 p-2 md:hidden" aria-label="Close navigation" onClick={() => setMobileNav(false)}><X className="h-4 w-4" /></button>}
        {/* Brand mark — Meridian mark + Manrope wordmark. */}
        <div className="flex items-center gap-2 px-4 pb-4 pt-5">
          <img
            src="/meridian-mark.svg"
            alt="Meridian"
            className="h-[22px] w-[22px]"
          />
          <span className="font-display text-[16px] font-bold tracking-[-0.01em] text-slate-950">
            Meridian
          </span>
          {customLogo && (
            <>
              <span className="h-5 w-px bg-slate-200" aria-hidden />
              <img
                src={customLogo}
                alt={me?.workspace?.name ?? "Workspace logo"}
                className="h-6 w-auto max-w-[88px] object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            </>
          )}
        </div>

        {/* Primary nav — grouped vertical lists. */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {(["workspace", "library"] as NavSection[]).map((section, sIdx) => {
            const items = visibleNav.filter((i) => i.section === section && primaryPaths.has(i.path));
            if (items.length === 0) return null;
            return (
              <div key={section} className={sIdx > 0 ? "mt-3 border-t border-slate-200 pt-3" : ""}>
                <p className="px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  {SECTION_LABELS[section]}
                </p>
                <div className="flex flex-col gap-0.5">
                  {items.map((item) => {
                    const { path, showsActivityDot } = item;
                    const Icon = navIcon(item.icon);
                    const label = item.label ?? t(item.labelKey!);
                    return (
                      <NavLink
                        key={path}
                        to={path}
                        end={path === "/"}
                        className={({ isActive }) =>
                          `relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors ${
                            isActive
                              ? "bg-slate-100 font-semibold text-slate-950"
                              : "text-slate-700 hover:bg-slate-50 hover:text-slate-950"
                          }`
                        }
                      >
                        <Icon className="h-4 w-4 shrink-0" strokeWidth={1.6} />
                        <span>{label}</span>
                        {showsActivityDot && activityCount > 0 && (
                          <span
                            aria-label={`${activityCount} updates`}
                            className="ml-auto inline-block h-1.5 w-1.5 rounded-full bg-rose-500"
                          />
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <details className="mt-4 border-t border-slate-200 pt-3" open={additionalNav.some(item => item.path === location.pathname) || undefined}>
            <summary className="cursor-pointer rounded-lg px-2.5 py-2 text-[13px] text-slate-600 focus-visible:outline-2">More tools</summary>
            <div className="mt-1 flex flex-col gap-1">
              {additionalNav.map(item => {
                const Icon = navIcon(item.icon);
                return <NavLink key={item.path} to={item.path} className={({isActive}) => `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] ${isActive ? "bg-slate-100 font-semibold text-slate-950" : "text-slate-600 hover:bg-slate-50"}`}><Icon className="h-4 w-4"/><span>{item.label ?? item.fallbackLabel}</span></NavLink>;
              })}
            </div>
          </details>
        </nav>

        {/* Utilities cluster — trial badge + language picker. */}
        <div className="flex items-center gap-1.5 border-t border-slate-100 px-4 py-2">
          <TrialBadge workspaceId={workspaceId} />
          {SUPPORTED_LOCALES.length > 1 && <LocalePicker />}
        </div>

        {/* Settings — anchors the sidebar foot. */}
        <div className="border-t border-slate-100 p-3">
          <FeedbackLauncher workspaceId={workspaceId} />
          <NavLink
            to="/administration"
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors ${
                isActive
                  ? "bg-slate-100 font-semibold text-slate-950"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-950"
              }`
            }
          >
            <Settings className="h-4 w-4 shrink-0" strokeWidth={1.6} />
            <span>Settings</span>
          </NavLink>
        </div>
      </aside>

      {/* MAIN CONTENT COLUMN. */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        {/* TOP BAR — Ask box on the left, utilities + account on the right. */}
        <header className="sticky top-0 z-20 flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-3 sm:gap-4 sm:px-7">
          <button className="shrink-0 rounded p-2 md:hidden" aria-label="Open navigation" aria-controls="main-navigation" aria-expanded={mobileNav} onClick={() => setMobileNav(true)}><Menu className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1"><AskBar visible /></div>

          <div className="flex items-center gap-1.5">
            <ExperienceToggle />
            {/* Notification bell sits LEFT of Refresh and stays visible even
                when the Companion layer is on (Peter, 2026-06-23): the bell
                carries forecast alerts + product news the Companion overlay
                doesn't surface, so the two coexist. */}
            {me && <NotificationBell />}
            <RefreshButton />
            {me && showWatch && !companionOn && <Watch workspaceId={workspaceId} />}
            {me?.user && (
              <AccountMenu
                isSuperuser={me.user.is_superuser}
                email={me.user.email}
                displayName={me.user.display_name ?? null}
                role={me.role}
                workspaceName={me.workspace?.name ?? ""}
                accent={accent}
                onLogout={() => void handleLogout()}
                onNavigate={(path) => navigate(path)}
              />
            )}
          </div>
        </header>

        <Suspense fallback={<p role="status" className="p-8 text-sm text-slate-500">Opening your view…</p>}><Outlet /></Suspense>

        {/* Floating sales prompt — turned off per Peter 2026-06-18. To
            re-enable, uncomment the line below (it auto-hides when the
            Companion is on, since they share the bottom-right corner). */}
        {/* {!companionOn && <SalesPrompt workspaceId={workspaceId} />} */}
        <Toaster position="bottom-right" richColors closeButton />
      </div>
    </div>
  );
}

// Refresh button — fires a window event that data-backed pages (Today,
// etc.) listen for to re-pull their content. Spins briefly for feedback.
function RefreshButton() {
  const [spinning, setSpinning] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        setSpinning(true);
        window.dispatchEvent(new Event("meridian:refresh"));
        window.setTimeout(() => setSpinning(false), 700);
      }}
      className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12.5px] font-medium text-slate-600 transition-colors hover:bg-slate-100"
      aria-label="Refresh"
    >
      <RotateCw className={`h-3.5 w-3.5 ${spinning ? "animate-spin" : ""}`} strokeWidth={1.8} />
      Refresh
    </button>
  );
}

// Account menu — compact user chip in the top bar with a downward
// dropdown holding the secondary surfaces + sign out.
function AccountMenu({
  isSuperuser,
  email,
  displayName,
  role,
  workspaceName,
  accent,
  onLogout,
  onNavigate,
}: {
  isSuperuser: boolean;
  email: string;
  displayName: string | null;
  role: string;
  workspaceName: string;
  accent: string;
  onLogout: () => void;
  onNavigate: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const initials =
    (displayName || email)
      .split(/[\s@]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("") || "?";
  const roleLabel = role ? role.charAt(0).toUpperCase() + role.slice(1) : workspaceName;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 transition-colors hover:bg-slate-100"
      >
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tracking-[0.04em] text-white"
          style={{ backgroundColor: accent }}
        >
          {initials}
        </span>
        <span className="hidden min-w-0 flex-col items-start leading-tight sm:flex">
          <span className="max-w-[140px] truncate text-[12.5px] font-semibold text-slate-950">
            {displayName || email}
          </span>
          <span className="max-w-[140px] truncate text-[10.5px] text-slate-500">{roleLabel}</span>
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          <div className="border-b border-slate-100 px-3 py-2.5">
            <p className="truncate text-[13px] font-medium text-slate-900">{displayName || email}</p>
            {displayName && <p className="truncate text-[11px] text-slate-500">{email}</p>}
            <p className="mt-1 text-[11px] text-slate-400">Workspace · {workspaceName}</p>
          </div>
          {isSuperuser && (
            <MenuItem icon={ShieldCheck} label="Platform" onClick={() => { setOpen(false); onNavigate("/platform"); }} />
          )}
          <MenuItem icon={Settings} label="Administration" onClick={() => { setOpen(false); onNavigate("/administration"); }} />
          <MenuItem icon={FileText} label="Metric definitions" onClick={() => { setOpen(false); onNavigate("/settings/metrics"); }} />
          <MenuItem icon={CreditCard} label="Pricing" onClick={() => { setOpen(false); onNavigate("/pricing"); }} />
          <MenuItem icon={MessageSquarePlus} label="Send feedback" onClick={() => { setOpen(false); window.dispatchEvent(new Event("meridian:feedback-open")); }} />
          <div className="border-t border-slate-100" />
          <MenuItem icon={LogOut} label="Sign out" onClick={() => { setOpen(false); onLogout(); }} />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Settings;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-slate-700 transition-colors hover:bg-slate-50"
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

// Compact locale picker — globe icon + current code, dropdown on click.
function LocalePicker() {
  const { locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const current = SUPPORTED_LOCALES.find((l) => l.code === locale);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Change language"
        className="flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
      >
        <Globe className="h-4 w-4" />
        <span className="text-[12px] uppercase">{current?.code ?? locale}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-20 mb-2 w-40 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          {SUPPORTED_LOCALES.map((l) => (
            <button
              key={l.code}
              type="button"
              role="menuitem"
              onClick={() => {
                setLocale(l.code);
                setOpen(false);
              }}
              className={[
                "flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors",
                l.code === locale ? "bg-slate-50 text-slate-900" : "text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              <span aria-hidden>{l.flag}</span>
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
